import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const BrandingContext = createContext({
  app_logo: null,
  app_name: "KPA Performance Manager",
  brand_primary: "#2C7BB6",
  brand_accent: "#F4A63A",
  brand_sidebar: "#0F172A",
  brand_font: "'Inter', sans-serif",
  refresh: async () => {},
});

const DEFAULT_TITLE = "KPA Performance Manager";
const DEFAULT_PRIMARY = "#2C7BB6";
const DEFAULT_ACCENT = "#F4A63A";
const DEFAULT_SIDEBAR = "#0F172A";
const DEFAULT_FONT = "'Inter', 'IBM Plex Sans', sans-serif";

const loadedFonts = new Set();
function loadGoogleFont(family) {
  if (!family || loadedFonts.has(family)) return;
  loadedFonts.add(family);
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, "+")}:wght@400;500;600;700;800&display=swap`;
  document.head.appendChild(link);
}

function applyTheme({ primary, accent, sidebar, font }) {
  const root = document.documentElement;
  if (primary) root.style.setProperty("--brand-primary", primary);
  if (accent) root.style.setProperty("--brand-accent", accent);
  if (sidebar) root.style.setProperty("--brand-sidebar", sidebar);
  if (font) {
    const familyName = font.split(",")[0].replace(/['"]/g, "").trim();
    loadGoogleFont(familyName);
    root.style.setProperty("--brand-font", font);
    document.body.style.fontFamily = font;
  }
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState({
    app_logo: null,
    app_name: DEFAULT_TITLE,
    brand_primary: DEFAULT_PRIMARY,
    brand_accent: DEFAULT_ACCENT,
    brand_sidebar: DEFAULT_SIDEBAR,
    brand_font: DEFAULT_FONT,
  });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      const next = {
        app_logo: data.app_logo || null,
        app_name: (data.app_name && data.app_name.trim()) || DEFAULT_TITLE,
        brand_primary: data.brand_primary || DEFAULT_PRIMARY,
        brand_accent: data.brand_accent || DEFAULT_ACCENT,
        brand_sidebar: data.brand_sidebar || DEFAULT_SIDEBAR,
        brand_font: data.brand_font || DEFAULT_FONT,
      };
      setBranding(next);
      // Title reflects the app name verbatim.
      document.title = next.app_name;
      applyTheme({
        primary: next.brand_primary,
        accent: next.brand_accent,
        sidebar: next.brand_sidebar,
        font: next.brand_font,
      });
    } catch { /* ignore — pre-login the endpoint returns 401 */ }
  }, []);

  useEffect(() => {
    document.title = DEFAULT_TITLE;
    applyTheme({ primary: DEFAULT_PRIMARY, accent: DEFAULT_ACCENT, sidebar: DEFAULT_SIDEBAR, font: DEFAULT_FONT });
    refresh();
  }, [refresh]);

  return <BrandingContext.Provider value={{ ...branding, refresh }}>{children}</BrandingContext.Provider>;
}

export const useBranding = () => useContext(BrandingContext);
